import React from 'react';
import { Table, TableBody, TableRow, TableCell, Divider, Box, Typography, Chip, Stack, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { OrderQRCode } from '../OrderForm/OrderQRCode';
import { Can } from '../../../../shared/components/Permission';
import { ROLES } from '../../../../shared/constants/roles';





interface OrderInfoProps {
  orderId: string;
  orderNumber: string;
  createdAt: string;
  origin: string;
  recipientName: string;
  receivedBy: string;
  deliveredBy?: string;
  deliveredAt?: string;
  deliveryDocument?: string;
  status?: string;
  userName?: string;
  pickupToken?: string;
  pickupPin?: string;
  orderCode?: string;
}

function translateStatus(status?: string) {
  if (status === 'PENDING' || status === 'RECEIVED') return 'Recebido';
  if (status === 'DELIVERED') return 'Entregue';
  if (status === 'CANCELLED') return 'Cancelado';
  return status || '-';
}

function statusColor(status?: string) {
  if (status === 'PENDING' || status === 'RECEIVED') return 'warning';
  if (status === 'DELIVERED') return 'success';
  if (status === 'CANCELLED') return 'error';
  return 'default';
}

function formatDate(date?: string) {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return date;
  }
}

function handleCopy(text?: string) {
  if (!text) return;
  navigator.clipboard.writeText(text);
}

export function OrderInfo(props: OrderInfoProps) {





  return (
    <Box>

      <Typography fontWeight={700} fontSize={16} mb={1.5}>Informações Gerais</Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 160, border: 'none', color: 'text.secondary' }}>Código</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.orderNumber}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Origem</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.origin}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Destinatário</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.recipientName}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Usuário</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.userName || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Status</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>
              <Chip
                label={translateStatus(props.status)}
                color={statusColor(props.status) as any}
                size="small"
                sx={{ fontWeight: 700, fontSize: 13 }}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      {/* Recebimento */}
      <Typography fontWeight={700} fontSize={16} mb={1.5}>Recebimento</Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 160, border: 'none', color: 'text.secondary' }}>Recebido por</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.receivedBy}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Recebido em</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{formatDate(props.createdAt)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      {/* Entrega */}
      <Typography fontWeight={700} fontSize={16} mb={1.5}>Entrega</Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 160, border: 'none', color: 'text.secondary' }}>Entregue por</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.deliveredBy || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Entregue em</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{formatDate(props.deliveredAt)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Documento de Entrega</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>{props.deliveryDocument || '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      {/* Retirada */}
      <Typography fontWeight={700} fontSize={16} mb={1.5}>Retirada</Typography>
      <Table size="small" sx={{ width: '100%' }}>
        <TableBody>
          <Can roles={[ROLES.ADMIN, ROLES.GESTOR]}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 160, border: 'none', color: 'text.secondary' }}>Pickup Token</TableCell>
              <TableCell sx={{ border: 'none', fontWeight: 500 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontFamily="monospace" fontSize={13}>{props.pickupToken}</Typography>
                  <Tooltip title="Copiar token">
                    <IconButton size="small" onClick={() => handleCopy(props.pickupToken)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          </Can>

          <TableRow>
            <TableCell sx={{ fontWeight: 600, border: 'none', color: 'text.secondary' }}>Pickup PIN</TableCell>
            <TableCell sx={{ border: 'none', fontWeight: 500 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={props.pickupPin}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 700, fontSize: 13, letterSpacing: 2 }}
                />
                <Tooltip title="Copiar PIN">
                  <IconButton size="small" onClick={() => handleCopy(props.pickupPin)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />
      <Can roles={[ROLES.ADMIN, ROLES.GESTOR]}>
        <OrderQRCode orderId={props.orderId} orderCode={props.orderCode} isDelivered={props.status === 'DELIVERED'} />
      </Can>


    </Box>
  );
}